import React, { useMemo, useState } from "react";
import { JsonView, allExpanded, darkStyles } from "react-json-view-lite";
import "react-json-view-lite/dist/index.css";
import { explainSearchDebugData } from "./explainSearchDebugData";
import AirportCode from "./AirportCode";
import "./SearchExplainer.css";

/**
 * Renders MainSearchController's test-mode debug JSON as a human-readable report, via one 3-way
 * tab:
 * - "Mini": summary stat cards + breakdown (pushed below the table via CSS `order`, panels
 *   pinned open by default), and ONE combined flat table across every bucket — Type / Source /
 *   Region / Route / Api / Environment / Match type / Description (info icon, hover for the
 *   full text) — with consecutive-duplicate cells blanked out instead of repeated.
 * - "Detailed": the original graphical dashboard — summary stat cards (hover-to-reveal), one
 *   card per bucket (kind, candidates checked vs qualified, by-api/by-region split), and a
 *   click-to-expand row list of every qualified hit (each row is a <details>/<summary>
 *   accordion — click it to reveal the routes it was matched against + full technical meta).
 * - "Developer": the complete raw backend payload (activeApiCodesData/parsedData/
 *   finalParsedData/requestStruct/countryCodes — everything), straight JSON, nothing hidden.
 *
 * See explainSearchDebugData.js for how the report/summary itself gets built.
 */
function SearchExplainer({ data }) {
  // One 3-way tab now: 'mini' / 'detailed' = the admin dashboard, either with every hover panel
  // pinned open inline by default ('mini') or hover-to-reveal as before ('detailed');
  // 'developer' = the full raw JSON payload.
  const [activeTab, setActiveTab] = useState("mini"); // 'mini' | 'detailed' | 'developer'
  const isDeveloper = activeTab === "developer";
  const alwaysOpen = activeTab === "mini";

  const report = useMemo(() => explainSearchDebugData(data), [data]);
  const { summary } = report;

  const noBucketsParsed = report.bucketReports.length === 0;

  // Mini view: all buckets' entries flattened into one combined table (instead of one table per
  // bucket), each row tagged with a "Type" (e.g. "Direct/Pure (Request1_INT-OW)") so it's still
  // clear which bucket a row came from. The Connecting-Airport (CA) bucket is kept in its own
  // separate table instead — its rows don't mix in with the rest.
  const allMiniRows = useMemo(
    () =>
      report.bucketReports.flatMap((bucket) =>
        bucket.isEmpty
          ? []
          : bucket.entries.map((entry) => ({
              typeLabel: bucket.kindLabel,
              isCa: bucket.key.includes("-CA"),
              entry,
            }))
      ),
    [report.bucketReports]
  );
  const miniRows = useMemo(() => allMiniRows.filter((r) => !r.isCa), [allMiniRows]);
  // The CA bucket only exists at all for oneway domestic/international searches — round-trip
  // and multiway searches never build a "-CA" bucket in the first place, so the CA table
  // shouldn't even appear (empty or otherwise) for those.
  const caBucketExists = useMemo(
    () => report.bucketReports.some((b) => b.key.includes("-CA")),
    [report.bucketReports]
  );
  const miniCaRows = useMemo(() => allMiniRows.filter((r) => r.isCa), [allMiniRows]);

  return (
    <div className="search-explainer">
      <div className="search-explainer-toolbar">
        <div className="search-explainer-summary">
          User region: <strong>{report.userRegion}</strong> &nbsp;·&nbsp;
          {report.requestStructCount} outbound API call
          {report.requestStructCount === 1 ? "" : "s"} would be made
        </div>

        <div className="search-explainer-mode-toggle">
          <button
            className={activeTab === "mini" ? "active" : ""}
            onClick={() => setActiveTab("mini")}
          >
            Mini
          </button>
          <button
            className={activeTab === "detailed" ? "active" : ""}
            onClick={() => setActiveTab("detailed")}
          >
            Detailed
          </button>
          <button
            className={activeTab === "developer" ? "active" : ""}
            onClick={() => setActiveTab("developer")}
          >
            Developer
          </button>
        </div>
      </div>

      {isDeveloper ? (
        <div className="search-explainer-body">
          <p className="search-explainer-kind-desc">
            Full raw backend payload for this test search — every field the controller returned,
            unmodified. Click any key/bracket to collapse/expand a branch.
          </p>
          <div className="search-explainer-json-viewer">
            <JsonView
              data={report.raw ?? data ?? {}}
              shouldExpandNode={allExpanded}
              style={darkStyles}
            />
          </div>
        </div>
      ) : (
        <div className={`search-explainer-body ${activeTab === "mini" ? "mini-view" : ""}`}>
          {/* ---- Summary dashboard ----
              Wrapped in its own element so mini-view can push it below the per-bucket cards via
              CSS `order`, without duplicating/reordering the actual JSX tree. */}
          <div className="search-explainer-stats-summary">
          <div className="search-explainer-stat-grid">
            <HoverInfo
              className="search-explainer-stat-card"
              panelRows={perBucketRows(summary.perBucket, (b) => b.candidateRequests)}
              footnote="Flat buckets count 1 per candidate route; the Connecting-Airport (CA) bucket counts 3 per connecting-airport medium (leg1, same-day leg2, next-day leg2)."
              alwaysOpen={alwaysOpen}
            >
              <div className="search-explainer-stat-value">
                {summary.totalCandidateRequests}
              </div>
              <div className="search-explainer-stat-label">Candidate routes checked</div>
            </HoverInfo>

            <HoverInfo
              className="search-explainer-stat-card"
              panelRows={perBucketRows(summary.perBucket, (b) => b.qualifiedHits)}
              footnote="A pipeline or direct-airline entry that passed every check and will actually be queried."
              alwaysOpen={alwaysOpen}
            >
              <div className="search-explainer-stat-value">{summary.totalQualifiedHits}</div>
              <div className="search-explainer-stat-label">Hits qualified</div>
            </HoverInfo>

            <HoverInfo
              className="search-explainer-stat-card"
              panelRows={perBucketRows(summary.perBucket, (b) => b.outboundApiCalls)}
              footnote="One row in requestStruct per (matching api, route) pair — this is the real number of HTTP calls that would be fired to TBO/Amadeus."
              alwaysOpen={alwaysOpen}
            >
              <div className="search-explainer-stat-value">
                {summary.totalOutboundApiCalls}
              </div>
              <div className="search-explainer-stat-label">Real API calls to be made</div>
            </HoverInfo>

            <HoverInfo
              className="search-explainer-stat-card"
              panelRows={[
                { label: "Pipeline hits", value: summary.bySource.pipeline },
                { label: "Direct-airline hits", value: summary.bySource.direct_airline },
              ]}
              footnote="Pipeline = matched via an api/pipeline configured for a region. Direct Airline = matched via the airline's own direct-route linking, independent of pipelines."
              alwaysOpen={alwaysOpen}
            >
              <div className="search-explainer-stat-value">
                {summary.bySource.pipeline} / {summary.bySource.direct_airline}
              </div>
              <div className="search-explainer-stat-label">
                Pipeline hits / Direct-airline hits
              </div>
            </HoverInfo>
          </div>

          <div className="search-explainer-breakdown-section-title">
            Real API calls to be made ({summary.totalOutboundApiCalls}) — one call per
            (qualified hit × matching candidate route) pair, e.g. Nearest-Airport hits get
            multiplied by every nearby-airport candidate:
          </div>
          <div className="search-explainer-breakdown-row">
            <BreakdownList
              title="Calls by bucket kind"
              counts={summary.callsByBucketKind}
              detailFor={(label) =>
                perBucketRows(
                  summary.perBucket.filter((b) => b.kindLabel === label && b.outboundApiCalls > 0),
                  (b) => b.outboundApiCalls
                )
              }
              footnoteFor={(label, count) =>
                `${count} of ${summary.totalOutboundApiCalls} total calls come from "${label}"-type ` +
                `bucket(s). ${
                  label === "Nearest Airport"
                    ? "Each qualified hit here gets multiplied by every nearby-airport candidate, which is why this number can be much bigger than the hit count."
                    : label === "Connecting Airport (CA)"
                    ? "Each qualified hit here gets multiplied by every connecting-hub leg (up to 3 per medium: leg1, same-day leg2, next-day leg2)."
                    : "This is the exact route(s) requested, so calls here roughly match the hit count."
                }`
              }
              alwaysOpen={alwaysOpen}
            />
            <BreakdownList
              title="Calls by API"
              counts={summary.callsByApi}
              detailFor={(api) =>
                perBucketRows(
                  summary.perBucket.filter((b) => b.callsByApi[api]),
                  (b) => b.callsByApi[api]
                )
              }
              footnoteFor={(api, count) =>
                `${count} of ${summary.totalOutboundApiCalls} total outbound calls will be sent to ${api}.`
              }
              alwaysOpen={alwaysOpen}
            />
            <BreakdownList
              title="Calls by region"
              counts={summary.callsByRegion}
              detailFor={(region) =>
                perBucketRows(
                  summary.perBucket.filter((b) => b.callsByRegion[region]),
                  (b) => b.callsByRegion[region]
                )
              }
              footnoteFor={(region, count) =>
                `${count} of ${summary.totalOutboundApiCalls} total calls are tied to a pipeline/direct-airline configured for region "${region}".`
              }
              alwaysOpen={alwaysOpen}
            />
          </div>
          </div>

          {activeTab === "mini" ? (
            <>
              <div className="search-explainer-mini-table-wrapper">
                <MiniRowTable rows={miniRows} />
              </div>
              {caBucketExists && (
                <div className="search-explainer-mini-table-wrapper">
                  <div className="search-explainer-breakdown-section-title">
                    Connecting Airport (CA) — kept separate since it's matched/merged
                    differently (medium-airport pairing) from the buckets above.
                  </div>
                  <MiniRowTable rows={miniCaRows} />
                </div>
              )}
            </>
          ) : (
            report.bucketReports.map((bucket) => {
            const bucketSummary = summary.perBucket.find((b) => b.key === bucket.key);
            return (
              <div className="search-explainer-bucket" key={bucket.key}>
                <div className="search-explainer-bucket-header">
                  <span className="search-explainer-bucket-name">{bucket.key}</span>
                  <span className="search-explainer-bucket-kind">{bucket.kindLabel}</span>
                  {activeTab !== "mini" && (
                    <HoverInfo
                      className={`search-explainer-bucket-count ${
                        bucket.isEmpty ? "empty" : ""
                      }`}
                      panelRows={[]}
                      emptyText=""
                      footnote={
                        bucketSummary
                          ? `"${bucket.entries.length} hits" = how many pipeline/direct-airline entries qualified. ` +
                            `"${bucketSummary.candidateRequests} route(s) checked" = how many candidate routes ` +
                            `existed in this bucket before filtering (for a flat bucket, usually 1; for Nearest ` +
                            `Airport, 1 per nearby airport candidate). A hit isn't tied to just one route — each ` +
                            `hit gets paired with every route it matches, which is why "${bucketSummary.outboundApiCalls}" ` +
                            `real outbound calls can be far more than either number. See "Matched Routes" in the ` +
                            `table below for exactly which routes each hit was paired with.`
                          : null
                      }
                    >
                      {bucket.entries.length} hit{bucket.entries.length === 1 ? "" : "s"}
                      {bucketSummary
                        ? ` across ${bucketSummary.candidateRequests} route${
                            bucketSummary.candidateRequests === 1 ? "" : "s"
                          } checked`
                        : ""}
                    </HoverInfo>
                  )}
                </div>

                {activeTab !== "mini" && (
                  <p className="search-explainer-kind-desc">{bucket.kindDescription}</p>
                )}

                {activeTab !== "mini" && bucket.countryCodes.length > 0 && (
                  <div className="search-explainer-country-codes">
                    Countries involved: {bucket.countryCodes.join(", ")}
                  </div>
                )}

                {bucketSummary &&
                  !bucket.isEmpty &&
                  bucketSummary.candidateRequests === 0 && (
                    <div className="search-explainer-no-candidates-warning">
                      ⚠ {bucket.entries.length} hit{bucket.entries.length === 1 ? "" : "s"} below
                      qualified purely on pipeline/airline config (region match, active status,
                      etc.) — but this bucket had <strong>0 candidate routes</strong> to begin
                      with (e.g. this destination has no connecting-airport route configured, or
                      no nearby-airport data). So even though {bucket.entries.length === 1
                        ? "it's"
                        : "they're"}{" "}
                      technically "included", {bucket.entries.length === 1 ? "it" : "none of them"}{" "}
                      will ever be paired with a real route or produce an actual API call here —
                      confirmed by "0 outbound calls" below.
                    </div>
                  )}

                {activeTab !== "mini" && bucketSummary && !bucket.isEmpty && (
                  <div className="search-explainer-bucket-mini-stats">
                    <span>
                      <strong>{bucketSummary.outboundApiCalls}</strong> outbound call
                      {bucketSummary.outboundApiCalls === 1 ? "" : "s"}
                    </span>
                    {Object.entries(bucketSummary.byApi).map(([api, count]) => (
                      <span key={api}>
                        <strong>{count}</strong> via {api}
                      </span>
                    ))}
                    {Object.entries(bucketSummary.byRegion).map(([region, count]) => (
                      <span key={region}>
                        <strong>{count}</strong> from {region}
                      </span>
                    ))}
                  </div>
                )}

                {bucket.isEmpty ? (
                  <div className="search-explainer-empty-note">{bucket.emptyReasonText}</div>
                ) : activeTab === "mini" ? (
                  <div className="search-explainer-table-scroll search-explainer-row-list">
                    <div className="search-explainer-row-grid search-explainer-row-grid-mini search-explainer-row-grid-head">
                      <span>Source</span>
                      <span>Region</span>
                      <span>Route</span>
                      <span>Api</span>
                      <span>Environment</span>
                      <span>Match type</span>
                      <span>Description</span>
                    </div>
                    {bucket.entries.map((entry, i) => {
                      const row = miniRowValues(entry);
                      const prevRow = i > 0 ? miniRowValues(bucket.entries[i - 1]) : null;

                      return (
                        <div className="search-explainer-row-grid search-explainer-row-grid-mini" key={i}>
                          <span
                            className={`search-explainer-source-badge ${entry.source}`}
                            style={prevRow && prevRow.source === row.source ? { visibility: "hidden" } : undefined}
                          >
                            {entry.source === "pipeline" ? "Pipeline" : "Direct Airline"}
                          </span>
                          <span>{prevRow && prevRow.region === row.region ? "" : row.region}</span>
                          <span>{prevRow && prevRow.route === row.route ? "" : row.route}</span>
                          <span>{prevRow && prevRow.api === row.api ? "" : row.api}</span>
                          <span>{prevRow && prevRow.env === row.env ? "" : row.env}</span>
                          <span>{prevRow && prevRow.matchType === row.matchType ? "" : row.matchType}</span>
                          <span className="search-explainer-info-icon-cell">
                            <HoverInfo
                              className="search-explainer-info-icon-wrap"
                              panelRows={[]}
                              emptyText=""
                              footnote={entry.adminReasonText}
                            >
                              <span className="search-explainer-info-icon" title="Why it was included">
                                i
                              </span>
                            </HoverInfo>
                          </span>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div className="search-explainer-table-scroll search-explainer-row-list">
                    <div className="search-explainer-row-grid search-explainer-row-grid-head">
                      <span>Source</span>
                      <span>Airline / API</span>
                      <span>Region</span>
                      <span>Why it was included</span>
                      <span>Routes</span>
                    </div>
                    {bucket.entries.map((entry, i) => (
                      <details className="search-explainer-row-accordion" key={i}>
                        <summary className="search-explainer-row-grid">
                          <span className="search-explainer-row-source-cell">
                            <span className="search-explainer-accordion-chevron">▸</span>
                            <span
                              className={`search-explainer-source-badge ${entry.source}`}
                            >
                              {entry.source === "pipeline" ? "Pipeline" : "Direct Airline"}
                            </span>
                          </span>
                          <span>{entry.label}</span>
                          <span>{entry.region}</span>
                          <span className="search-explainer-why-cell">
                            {entry.adminReasonText}
                          </span>
                          <span className="search-explainer-row-routes-cell">
                            {entry.matchedRoutesCount} route
                            {entry.matchedRoutesCount === 1 ? "" : "s"}
                          </span>
                        </summary>

                        <div className="search-explainer-row-panel">
                          <div className="search-explainer-row-panel-title">
                            Matched routes — every route below will be sent as a separate real
                            API call to <strong>{entry.label}</strong>. Hover an airport code for
                            its full name/city/country.
                          </div>
                          {entry.matchedRoutesCount > 0 ? (
                            <div className="search-explainer-route-cards">
                              {entry.matchedRoutes.map((r, idx) => (
                                <div className="search-explainer-route-card" key={idx}>
                                  <span className="search-explainer-route-endpoint">
                                    <AirportCode code={r.origin} />
                                  </span>
                                  <span className="search-explainer-route-arrow">→</span>
                                  <span className="search-explainer-route-endpoint">
                                    <AirportCode code={r.destination} />
                                  </span>
                                  {r.isNearby && (
                                    <span className="search-explainer-route-tag">
                                      nearby airport
                                      {r.nearestDistanceKm != null
                                        ? ` · ${r.nearestDistanceKm} km`
                                        : ""}
                                    </span>
                                  )}
                                  {r.legNote && (
                                    <span className="search-explainer-route-tag">
                                      {r.legNote}
                                    </span>
                                  )}
                                  {r.date && (
                                    <span className="search-explainer-route-date">{r.date}</span>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="search-explainer-empty-note">
                              This hit qualified, but no candidate route in this bucket ended up
                              matching it in the final request build.
                            </div>
                          )}

                          <div className="search-explainer-row-panel-meta">
                            <span>
                              Data ID <strong>{entry.dataId ?? "—"}</strong>
                            </span>
                            {entry.regionLinkId != null && (
                              <span>
                                pipeline_regions ID <strong>{entry.regionLinkId}</strong>
                              </span>
                            )}
                            <span>
                              Match type <strong>{entry.type}</strong>
                            </span>
                            <span>
                              Environments <strong>{entry.environmentsSupported || "—"}</strong>
                            </span>
                            <span>
                              Ref IDs{" "}
                              <strong>
                                {Array.isArray(entry.refIds)
                                  ? entry.refIds.join(", ")
                                  : entry.refIds ?? "—"}
                              </strong>
                            </span>
                            <span>
                              Token <strong>{entry.tokenPresent ? "present" : "missing"}</strong>
                            </span>
                          </div>
                        </div>
                      </details>
                    ))}
                  </div>
                )}

                {/* "Why NOT included" — temporarily disabled per feedback (kept in code, not
                    removed, in case it's wanted back later). Flip `false` back to the real
                    condition to re-enable. */}
                {false &&
                  (bucket.rejectedPipelines.length > 0 ||
                  bucket.rejectedDirectAirlines.length > 0) && (
                  <details className="search-explainer-rejected-section">
                    <summary>
                      Why NOT included —{" "}
                      {bucket.rejectedPipelines.length + bucket.rejectedDirectAirlines.length}{" "}
                      candidate
                      {bucket.rejectedPipelines.length + bucket.rejectedDirectAirlines.length === 1
                        ? ""
                        : "s"}{" "}
                      considered and rejected
                      <span className="search-explainer-accordion-chevron">▸</span>
                    </summary>
                    <div className="search-explainer-rejected-list">
                      {bucket.rejectedPipelines.map((r, i) => (
                        <div className="search-explainer-rejected-item" key={`p${i}`}>
                          <div className="search-explainer-rejected-item-head">
                            <span className="search-explainer-source-badge pipeline">
                              Pipeline
                            </span>
                            <span className="search-explainer-rejected-item-label">
                              {r.label}
                            </span>
                            <span className="search-explainer-rejected-item-type">{r.type}</span>
                          </div>
                          <div className="search-explainer-rejected-item-reason">
                            {r.reasonText}
                          </div>
                        </div>
                      ))}
                      {bucket.rejectedDirectAirlines.map((r, i) => (
                        <div className="search-explainer-rejected-item" key={`d${i}`}>
                          <div className="search-explainer-rejected-item-head">
                            <span className="search-explainer-source-badge direct_airline">
                              Direct Airline
                            </span>
                            <span className="search-explainer-rejected-item-label">
                              {r.label}
                            </span>
                          </div>
                          <div className="search-explainer-rejected-item-reason">
                            {r.reasonText}
                          </div>
                        </div>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            );
          })
          )}
        </div>
      )}

      {noBucketsParsed && (
        <div className="search-explainer-empty-note" style={{ margin: "0 20px 16px" }}>
          Couldn't recognize any pipeline/direct-airline buckets in this response — switch to
          Developer mode to see the full raw backend payload.
        </div>
      )}
    </div>
  );
}

/**
 * Computes the mini-table's per-column display values for one entry — used both to render a
 * row and to compare it against the previous row so consecutive-duplicate cells (same region,
 * same route, etc. across adjacent rows in one bucket) can be blanked out instead of repeated.
 */
function miniRowValues(entry) {
  return {
    source: entry.source,
    region: entry.region,
    route:
      entry.matchedRoutesCount > 0
        ? entry.matchedRoutes.map((r) => `${r.origin}→${r.destination}`).join(", ")
        : "—",
    api: entry.label,
    env: entry.environmentsSupported || "—",
    matchType: entry.type,
  };
}

/**
 * Mini view's flat table — Type / Source / Region / Route / Api / Environment / Match type /
 * Description(info icon). Consecutive rows with an identical value in a column have that cell
 * blanked out instead of repeating it. Used once for the regular buckets and once more,
 * separately, for the Connecting-Airport (CA) bucket.
 */
function MiniRowTable({ rows }) {
  return (
    <div className="search-explainer-table-scroll search-explainer-row-list search-explainer-row-list-mini">
      <div className="search-explainer-row-grid search-explainer-row-grid-mini search-explainer-row-grid-head">
        <span>Type</span>
        <span>Source</span>
        <span>Region</span>
        <span>Route</span>
        <span>Api</span>
        <span>Environment</span>
        <span>Match type</span>
        <span>Description</span>
      </div>
      {rows.length === 0 && (
        <div className="search-explainer-empty-note" style={{ padding: "10px 14px" }}>
          No hits qualified here.
        </div>
      )}
      {rows.map(({ typeLabel, entry }, i) => {
        const row = { type: typeLabel, ...miniRowValues(entry) };
        const prevItem = i > 0 ? rows[i - 1] : null;
        const prevRow = prevItem
          ? { type: prevItem.typeLabel, ...miniRowValues(prevItem.entry) }
          : null;
        // Dedup only makes sense within the same Type group — a new bucket's first row must
        // always show its own Source/Region even if it happens to match the previous group's
        // last row (e.g. two consecutive buckets both starting with "India").
        const sameGroup = prevRow != null && prevRow.type === row.type;
        return (
          <div className="search-explainer-row-grid search-explainer-row-grid-mini" key={i}>
            <span>{sameGroup ? "" : row.type}</span>
            <span
              className={`search-explainer-source-badge ${entry.source}`}
              style={sameGroup && prevRow.source === row.source ? { visibility: "hidden" } : undefined}
            >
              {entry.source === "pipeline" ? "Pipeline" : "Direct Airline"}
            </span>
            <span>{sameGroup && prevRow.region === row.region ? "" : row.region}</span>
            <span>{row.route}</span>
            <span>{row.api}</span>
            <span>{row.env}</span>
            <span>{row.matchType}</span>
            <span className="search-explainer-info-icon-cell">
              <HoverInfo
                className="search-explainer-info-icon-wrap"
                panelRows={[]}
                emptyText=""
                footnote={entry.adminReasonText}
              >
                <span className="search-explainer-info-icon" title="Why it was included">
                  i
                </span>
              </HoverInfo>
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** Turns a list of perBucket rows into { label, value } pairs for a HoverInfo panel. */
function perBucketRows(perBucket, getValue) {
  return perBucket
    .map((b) => ({ label: b.key, value: getValue(b) }))
    .filter((row) => row.value);
}

/**
 * Custom (non-native) hover panel — used for the summary dashboard (stat cards, breakdown-list
 * numbers, per-bucket header count): hovering shows a floating panel with exactly which buckets
 * contributed to that number. Kept as hover here (rather than the click accordion below) since
 * that's how the user wants this specific section to behave.
 */
function HoverInfo({
  className = "",
  panelRows = [],
  plainList = null,
  emptyText = "No contributing buckets.",
  footnote,
  children,
  alwaysOpen = false,
}) {
  return (
    <div
      className={`search-explainer-hover-wrap ${className} ${alwaysOpen ? "always-open" : ""}`}
    >
      {children}
      <div
        className={`search-explainer-hover-panel ${alwaysOpen ? "always-open" : ""}`}
      >
        {plainList ? (
          plainList.length > 0 ? (
            <ul className="search-explainer-hover-panel-plain-list">
              {plainList.map((text, i) => (
                <li key={i}>{text}</li>
              ))}
            </ul>
          ) : (
            emptyText && <div className="search-explainer-hover-panel-empty">{emptyText}</div>
          )
        ) : panelRows.length > 0 ? (
          <ul className="search-explainer-hover-panel-rows">
            {panelRows.map((row) => (
              <li key={row.label}>
                <span>{row.label}</span>
                <span>{row.value}</span>
              </li>
            ))}
          </ul>
        ) : (
          emptyText && <div className="search-explainer-hover-panel-empty">{emptyText}</div>
        )}
        {footnote && <div className="search-explainer-hover-panel-footnote">{footnote}</div>}
      </div>
    </div>
  );
}

function BreakdownList({ title, counts, detailFor, footnoteFor, alwaysOpen = false }) {
  const entries = Object.entries(counts || {});
  return (
    <div className="search-explainer-breakdown">
      <div className="search-explainer-breakdown-title">{title}</div>
      {entries.length === 0 ? (
        <div className="search-explainer-breakdown-empty">—</div>
      ) : (
        <ul>
          {entries.map(([label, count]) => (
            <li key={label} className={alwaysOpen ? "always-open" : ""}>
              <span>{label}</span>
              <HoverInfo
                className="search-explainer-hover-wrap-inline"
                panelRows={detailFor ? detailFor(label) : []}
                footnote={footnoteFor ? footnoteFor(label, count) : null}
                alwaysOpen={alwaysOpen}
              >
                <span className="search-explainer-breakdown-count">{count}</span>
              </HoverInfo>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default SearchExplainer;
